/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import InteractiveDialog from '../../pures/InteractiveDialog/InteractiveDialog'
import PdfReader from '../../pures/adobePfdReader/PdfReader'
import { AIDocumentAnalysisPure } from "../../pures"
import { DigitalVaultDocumentService, LocalStorageService } from '../../../services'

/**
 * @typedef {Object} Props
 * @property {boolean} showDialog - Flag to show/hide the dialog
 * @property {Object} documentData - The document data object containing metadata
 * @property {string} documentUrl - The URL of the document to analyze
 * @property {string} selectedFileBase64 - Base64 string of the selected file
 * @property {number|null} signDocumentId - Optional ID from signDocuments array
 * @property {string|null} signDocumentUrl - Optional URL from signDocuments
 * @property {function} onClose - Handler for closing the dialog
 * @param {Props} props - The component props
 * @returns {JSX.Element} The rendered component
 */
const AIDocumentAnalysisAction = ({
    showDialog,
    documentData,
    documentUrl,
    selectedFileBase64,
    signDocumentId = null,
    signDocumentUrl = null,
    onClose
}) => {
    const { t } = useTranslation()

    // Estados para el chat
    const [chatMessages, setChatMessages] = useState([])
    const [userMessage, setUserMessage] = useState('')
    const [isSending, setIsSending] = useState(false)
    const [blobUrl, setBlobUrl] = useState('')
    const [fullDocumentData, setFullDocumentData] = useState(null)
    const [isLoadingDocument, setIsLoadingDocument] = useState(false)

    useEffect(() => {
        const fetchFullDocument = async () => {
            if (!documentData?.id) return

            try {
                setIsLoadingDocument(true)
                const url = process.env.REACT_APP_MIFIRMA_API
                const localStorageService = new LocalStorageService()
                const token = localStorageService.getLocalStorageItem("token").idToken
                const digitalVaultService = new DigitalVaultDocumentService(url)
                const response = await digitalVaultService.getDocumentById(documentData.id, token)

                setFullDocumentData(response.data)
            } catch (error) {
                console.log("Error:", error)
                setFullDocumentData(documentData)
            } finally {
                setIsLoadingDocument(false)
            }
        }

        fetchFullDocument()
    }, [documentData?.id])

    // Generar PDF viewer
    const pdfViewer = useMemo(
        () => {
            if (selectedFileBase64) {
                // Si hay un archivo base64, usarlo
                return (
                    <PdfReader
                        className='pdfReader'
                        file={selectedFileBase64}
                        isFile
                        src={`data:application/pdf;base64,${selectedFileBase64}`}
                    />
                )
            } else if (documentUrl) {
                // Si hay una URL, usarla
                return (
                    <PdfReader
                        className='pdfReader'
                        url={documentUrl}
                        awsURL
                        src={documentUrl}
                    />
                )
            }
            return null
        },
        [selectedFileBase64, documentUrl]
    )

    // Handler para cerrar el diálogo
    const handleClose = useCallback(() => {
        if (onClose) onClose()
    }, [onClose])

    // Generar blob URL si hay base64
    useMemo(() => {
        if (selectedFileBase64) {
            try {
                const byteCharacters = atob(selectedFileBase64)
                const byteNumbers = new Array(byteCharacters.length)
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i)
                }
                const byteArray = new Uint8Array(byteNumbers)
                const blob = new Blob([byteArray], { type: 'application/pdf' })
                const url = URL.createObjectURL(blob)
                setBlobUrl(url)
            } catch (error) {
                console.error('Error creating blob URL:', error)
            }
        }
    }, [selectedFileBase64])

    if (!showDialog) return null

    const dataToUse = fullDocumentData || documentData

    return (
        <InteractiveDialog
            title={documentData?.name}
            onClose={handleClose}
            dialogSize='XG'
        >
            <AIDocumentAnalysisPure
                documentData={dataToUse}
                documentUrl={documentUrl}
                blobUrl={blobUrl}
                pdfViewer={pdfViewer}
                chatMessages={chatMessages}
                userMessage={userMessage}
                isSending={isSending}
                signDocumentId={signDocumentId}
                signDocumentUrl={signDocumentUrl}
                onClose={handleClose}
            />
        </InteractiveDialog>
    )
}

export default AIDocumentAnalysisAction
